import type * as React from "react"
import "./Typography.css"

/**
 * The type roles, as components. Each one names a job — display title, section
 * head, body, lede, eyebrow, inline data — so a caller picks a role rather than
 * a size, and the restraint rules hold by construction:
 *
 *  - serif (`--font-serif`) for titles AND prose (sans carries console chrome only,
 *    operator ruling 2026-09-07), mono
 *    (`--font-mono`) for everything structural (eyebrows, stats, keys) —
 *    UPPERCASE and tracked where it labels rather than reads.
 *  - Weight floor 400; 400/500 only, never a third weight in one family.
 *  - Color is rationed to meaning: prose stays on ink, accent belongs to the
 *    Eyebrow and the PullQuote rule.
 *  - Italic is not a generic emphasis prop. It lives only in PullQuote and
 *    Epigraph, the two explicit editorial roles.
 *
 * `className` is deliberately omitted from every prop type: the role IS the
 * styling contract, so a call site cannot re-skin one instance and drift the
 * role. Layout around a role belongs to its container.
 */

/** HTML attributes minus `className` — the roles own their own class. */
type Attrs<E> = Omit<React.HTMLAttributes<E>, "className">

export interface DisplayProps extends Attrs<HTMLHeadingElement> {}

/** Display — the largest serif title (h1). Weight 500, tight leading. */
export function Display(props: DisplayProps) {
  return <h1 className="FfnDisplay" {...props} />
}

/** Heading levels; each maps to both an element and a serif size step. */
export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6

export interface HeadingProps extends Attrs<HTMLHeadingElement> {
  /** Heading level h1-h6; drives the tag and the serif size step. @default 2 */
  readonly level?: HeadingLevel
  /**
   * Take the SIZE STEP from this level instead of from `level`, leaving `level` to set the
   * element and therefore the document outline. Omit it and the two stay locked together,
   * which is the contract for every ordinary call.
   *
   * ── WHEN THIS IS THE RIGHT ANSWER, AND WHY IT EXISTS AT ALL ──
   *
   * A heading level is TWO facts wearing one number: where the heading sits in the outline,
   * and how big it is. They usually agree. They stop agreeing when a component is dropped
   * into a document whose nearest ancestor heading is shallower than the component's own
   * type scale wants — `AutonomyCertLadder`'s rungs are h4-SIZED entries under an h2 section
   * band, so locking them together forced a choice between an outline that skips h3 (axe
   * `heading-order`, moderate, reported on /gallery/instruments 2026-08-16) and a size step
   * two stops too loud for a list entry.
   *
   * This prop refuses the choice: `level={3} sizeLevel={4}` renders `<h3 data-level="4">`,
   * so the outline is sequential and the type is unchanged. It is DELIBERATELY not a
   * `fontSize` prop — the value is still a level on the same scale, so a call site can only
   * pick a step the system already defines, and the type roles keep their "the role IS the
   * styling contract" property.
   *
   * Reach for it only when a real outline conflict forces it, and say which heading it is
   * nesting under when you do.
   */
  readonly sizeLevel?: HeadingLevel
}

const HEADING_TAGS = {
  1: "h1",
  2: "h2",
  3: "h3",
  4: "h4",
  5: "h5",
  6: "h6"
} as const satisfies Record<HeadingLevel, keyof React.JSX.IntrinsicElements>

/**
 * Heading — serif section heads across the full h1-h6 range. The level picks
 * the element (so the document outline stays honest) and is mirrored onto
 * `data-level`, which is what the size step keys off in CSS.
 *
 * `sizeLevel` decouples the two when a nesting context forces it; see its doc comment.
 */
export function Heading({ level = 2, sizeLevel, ...props }: HeadingProps) {
  const Tag = HEADING_TAGS[level]
  return <Tag className="FfnHeading" data-level={sizeLevel ?? level} {...props} />
}

export interface BodyProps extends Attrs<HTMLParagraphElement> {}

/** Body — the serif body face at the 400 weight floor, relaxed leading. */
export function Body(props: BodyProps) {
  return <p className="FfnBody" {...props} />
}

export interface LedeProps extends Attrs<HTMLParagraphElement> {}

/** Lede — a larger serif intro paragraph in muted ink; still weight 400. */
export function Lede(props: LedeProps) {
  return <p className="FfnLede" {...props} />
}

export interface EyebrowProps extends Attrs<HTMLSpanElement> {}

/**
 * Eyebrow — mono, UPPERCASE, tracked, in the accent. A structural label above
 * a heading, never a second heading: it must add information the heading does
 * not (see SectionKicker, which drops a kicker that only echoes its title).
 */
export function Eyebrow(props: EyebrowProps) {
  return <span className="FfnEyebrow" {...props} />
}

export interface MonoProps extends Attrs<HTMLSpanElement> {}

/** Mono — inline monospace for data, keys and identifiers, in muted ink. */
export function Mono(props: MonoProps) {
  return <span className="FfnMono" {...props} />
}

export interface StatProps extends Attrs<HTMLSpanElement> {}

/** Stat — a large mono numeric keystone value, tabular so columns align. */
export function Stat(props: StatProps) {
  return <span className="FfnStat" {...props} />
}

export interface PullQuoteProps extends Attrs<HTMLQuoteElement> {}

/**
 * PullQuote — an editorial role where italic IS allowed: serif italic in muted
 * ink behind an accent left rule.
 */
export function PullQuote(props: PullQuoteProps) {
  return <blockquote className="FfnPullQuote" {...props} />
}

export interface EpigraphProps extends Attrs<HTMLParagraphElement> {}

/** Epigraph — the other allowed italic: a serif dateline/deck lede, muted. */
export function Epigraph(props: EpigraphProps) {
  return <p className="FfnEpigraph" {...props} />
}
